import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell, Stat } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Clock, PhoneCall, UserPlus, FileEdit } from 'lucide-react';

const ROLE_COLOR = { admin: 'bg-primary/15 text-primary border-primary/30', manager: 'bg-amber-500/15 text-amber-600 border-amber-500/30', hr: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' };
const EMPTY = { email: '', password: '', name: '', role: 'hr', phone: '', designation: '' };
const ACTION_LABEL = {
  opened_profile: 'Opened profile',
  created_candidate: 'Added lineup',
  updated_candidate: 'Updated lineup',
  logged_call: 'Logged call',
  scheduled_interview: 'Scheduled interview',
  uploaded_cv: 'Uploaded CV',
  created_call_candidate: 'Added manual candidate',
};
const ACTION_ICON = {
  logged_call: PhoneCall, created_candidate: UserPlus, created_call_candidate: UserPlus,
  updated_candidate: FileEdit, uploaded_cv: UserPlus, scheduled_interview: Clock,
  opened_profile: FileEdit,
};
function fmtIST(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }); }
  catch { return iso; }
}
const isoToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export default function Team() {
  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState({});
  const [activity, setActivity] = useState([]);
  const [eod, setEod] = useState({ items: [] });
  const [eodDate, setEodDate] = useState(isoToday());
  const [drawerUid, setDrawerUid] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const [{ data: users }, { data: tgts }] = await Promise.all([api.get('/users'), api.get('/call-targets')]);
      setRows(users);
      setTargets(Object.fromEntries((tgts || []).map(t => [t.user_id, t.daily_target || 0])));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadEod = async () => {
    try { const { data } = await api.get('/eod-summaries', { params: { date: eodDate } }); setEod(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadActivity = async (uid) => {
    if (!uid) return setActivity([]);
    try { const { data } = await api.get('/activity', { params: { user_id: uid, limit: 100 } }); setActivity(data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadEod(); /* eslint-disable-next-line */ }, [eodDate]);
  useEffect(() => {
    const uid = searchParams.get('user');
    setDrawerUid(uid);
    loadActivity(uid);
  }, [searchParams]);

  const openDrawer = (uid) => { setSearchParams({ user: uid }); };
  const closeDrawer = () => { setSearchParams({}); };

  const create = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('Fill email, name & password'); return; }
    try { await api.post('/auth/register', form); toast.success('User created'); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const toggleActive = async (u) => { try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); } catch (e) { toast.error(formatErr(e)); } };
  const del = async (u) => { if (!window.confirm(`Delete ${u.name}?`)) return; try { await api.delete(`/users/${u.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error(formatErr(e)); } };
  const saveTarget = async (uid, value) => {
    const v = parseInt(value, 10);
    if (Number.isNaN(v) || v < 0) { toast.error('Invalid target'); return; }
    try { await api.put(`/call-targets/${uid}`, { daily_target: v }); toast.success('Target saved'); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const drawerUser = useMemo(() => rows.find(u => u.id === drawerUid), [rows, drawerUid]);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Admin"
        title="Team management"
        subtitle="Onboard your team, set daily call targets, and review each recruiter's activity + end-of-day report."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-user-btn" className="bg-primary hover:bg-primary/90 text-white h-11"><Plus className="w-4 h-4 mr-2" />Add user</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">Add team member</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Temp password</Label><Input data-testid="user-pw" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="hr">HR / Recruiter</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-span-2"><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
                <Button data-testid="user-save" onClick={create} className="col-span-2 bg-primary hover:bg-primary/90 text-white">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Team + daily targets */}
      <div className="tactical-card overflow-hidden mb-6">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
            <TableHead>Daily call target</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="users-tbody">
            {rows.map(u => (
              <TableRow key={u.id} className="cursor-pointer hover:bg-accent/30" onClick={() => openDrawer(u.id)}>
                <TableCell className="font-medium">{u.name}<div className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.designation || '—'}</div></TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline" className={ROLE_COLOR[u.role]}>{u.role}</Badge></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {u.role !== 'admin' ? (
                    <div className="flex items-center gap-1">
                      <Input type="number" min={0} defaultValue={targets[u.id] || 0} data-testid={`target-input-${u.id}`}
                        onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v !== (targets[u.id] || 0)) saveTarget(u.id, v); }}
                        className="w-24 h-9 text-sm" />
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">calls/day</span>
                    </div>
                  ) : '—'}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}><button onClick={() => toggleActive(u)} className={`text-xs font-medium ${u.active ? 'text-emerald-600' : 'text-muted-foreground'}`}>{u.active ? 'Active' : 'Disabled'}</button></TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => del(u)} className="text-red-500 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* End of Day Summary */}
      <div className="tactical-card p-5 mb-6" data-testid="eod-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Auto-generated at 6 pm IST (viewable anytime)</div>
            <div className="font-display text-xl font-black tracking-tight">End-of-day summary</div>
          </div>
          <Input type="date" value={eodDate} onChange={(e) => setEodDate(e.target.value)} data-testid="eod-date" className="w-44" />
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Recruiter</TableHead>
            <TableHead>Calls</TableHead>
            <TableHead>Candidates added</TableHead>
            <TableHead>Profiles updated</TableHead>
            <TableHead>Interviews</TableHead>
            <TableHead>Idle time</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="eod-tbody">
            {(eod.items || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No activity for {eodDate}.</TableCell></TableRow>}
            {(eod.items || []).map(it => (
              <TableRow key={it.user_id} data-testid={`eod-row-${it.user_id}`}>
                <TableCell className="font-medium">{it.user_name}<div className="text-[10px] uppercase tracking-widest text-muted-foreground">{it.role}</div></TableCell>
                <TableCell className="font-mono font-black">{it.calls}</TableCell>
                <TableCell className="font-mono">{it.candidates_added}</TableCell>
                <TableCell className="font-mono">{it.profiles_updated}</TableCell>
                <TableCell className="font-mono">{it.interviews_scheduled}</TableCell>
                <TableCell className="font-mono">{it.idle_minutes}m</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Activity drawer */}
      <Dialog open={!!drawerUid} onOpenChange={(v) => { if (!v) closeDrawer(); }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-xl sm:text-2xl font-black">{drawerUser?.name || 'Recruiter'} · activity log</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">{drawerUser?.email} · {drawerUser?.role} · last 100 actions</div>
          </DialogHeader>
          <div className="space-y-2 max-h-[68vh] overflow-y-auto" data-testid="activity-log">
            {activity.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">No activity recorded yet.</div>}
            {activity.map(a => {
              const Icon = ACTION_ICON[a.action] || FileEdit;
              return (
                <div key={a.id} data-testid={`activity-${a.id}`} className="border border-border/70 rounded-md p-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center flex-shrink-0"><Icon className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{ACTION_LABEL[a.action] || a.action}{a.target_name ? ` · ${a.target_name}` : ''}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{fmtIST(a.ts)}</div>
                    {a.meta && Object.keys(a.meta).length > 0 && <div className="text-[11px] text-muted-foreground mt-0.5">{Object.entries(a.meta).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
